import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  List,
  ListSubheader,
  ListItem,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect, useMemo } from "react";
import {
  DatePicker,
  LocalizationProvider,
  TimeField,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { blue } from "@mui/material/colors";
import { USER_FIELDS } from "../../services/user/userApi";

const hdrs = {
  // email: { type: String, alias: "Account" },
  birth: { type: "Date", g: 1 },
  phone: { type: "String", g: 1 },
  zalo: { type: "String", g: 1 },
  facebook: { type: "String", g: 1 },
  gmail: { type: "String", g: 1 },
  address: { type: "String", g: 1 }, //dia chi NB
  address2: { type: "String", g: 1 }, //dia chi VN
  ngayVaoDaoTrang: { type: "Date", g: 2 },
  ngayLenGieoDuyen: { type: "Date", g: 2 },
  ngayLenTuyHy: { type: "Date", g: 2 },
  ngayLenDuThinh: { type: "Date", g: 2 },
  ngayLenChinhThuc: { type: "Date", g: 2 },
  phapDanh: { type: "String", g: 1 },
  thanhPhan: { type: "String", g: 2 }, // A, B, C , D
  ngheNghiep: { type: "String", g: 3 },
  kyNang: { type: "String", g: 3 },
  tenFb: { type: "String", g: 3 },
  cachDTT: { type: "String", g: 3 },
  bach7: { type: "String", g: 3 }, // 0:chưa|1:đang|2:bạch xong
  bach49: { type: "String", g: 3 },
  bach108: { type: "String", g: 3 },
  nguoiHuongDan: { type: "String", g: 3 },
};
const userInfoGroups = {
  "Cơ bản": [
    { key: "birth", type: "Date" },
    { key: "phapDanh", type: "String", g: 1 },
    { key: "phone", type: "String" },
    { key: "fb", type: "String" },
    { key: "tp", type: "String", g: 2 }, // A, B, C , D
    { key: "address", type: "String" }, //dia chi NB
  ],
  "Bổ sung": [
    { key: "gmail", type: "String" },
    { key: "zalo", type: "String" },
    { key: "address2", type: "String" }, //dia chi VN
  ],
  "Thông tin khác": [
    { key: "ngayVaoDaoTrang", type: "Date", g: 2 },
    { key: "ngayLenGieoDuyen", type: "Date", g: 2 },
    { key: "ngayLenTuyHy", type: "Date", g: 2 },
    { key: "ngayLenDuThinh", type: "Date", g: 2 },
    { key: "ngayLenChinhThuc", type: "Date", g: 2 },
    { key: "ngheNghiep", type: "String", g: 3 },
    { key: "kyNang", type: "String", g: 3 },
    { key: "tenFb", type: "String", g: 3 },
    { key: "cachDTT", type: "String", g: 3 },
    { key: "bach7", type: "String" }, // 0:chưa|1:đang|2:bạch xong
    { key: "bach49", type: "String" }, // 0:chưa|1:đang|2:bạch xong
    { key: "bach108", type: "String" }, // 0:chưa|1:đang|2:bạch xong
    { key: "nguoiHuongDan", type: "String" }, // 0:chưa|1:đang|2:bạch xong
  ],
};
Object.entries(userInfoGroups).forEach(([groupName, cols])=>{
  cols.forEach(col=>{
    var field = USER_FIELDS.find(f=>f.key === col.key);
    if (field) {col.alias = field.alias;}
  });
});
export {userInfoGroups};
export default function EditUserDialog({ open, user, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [originalUser, setOriginalUser] = useState({});

  // Reset form when dialog opens or user changes
  useEffect(() => {
    if (user) {
      setForm(user);
      setOriginalUser(user);
    }
  }, [user, open]);

  // ✅ Compute changed fields only
  const changes = useMemo(() => {
    const diff = {};
    Object.keys(form || {}).forEach((key) => {
      if (form[key] !== originalUser[key]) {
        diff[key] = form[key];
      }
    });
    return diff;
  }, [form, originalUser]);

  const hasChanges = Object.keys(changes).length > 0;

  // ✅ Save only changed data
  const handleSave = () => {
    if (!hasChanges) return;
    onSave(changes);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Edit User
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          maxHeight: 600,
          overflow: "auto",
        }}
      >
        <EditUserForm
          user={form}
          onChange={(key, value) => {
            setForm((prev) => ({ ...prev, [key]: value }));
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={!hasChanges} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EditUserForm({ user, onChange }) {
  // Handle input change
  const handleInputChange = (raw, key, type) => {
    onChange(key, raw);
  };
  const handleDateChange = (day, key, type) => {
    onChange(key, day ? day.format("YYYY-MM-DD") : "");
  };
  const handleTimeChange = (day, key, type) => {
    onChange(key, day ? day.format("hh:mm") : "");
  };

  return (
    <List sx={{ width: "100%", bgcolor: "background.paper" }}>
      {Object.entries(userInfoGroups).map(([groupName, cols]) => (
        <Box key={groupName}>
          {/* Group Header */}
          <ListSubheader
            component="div"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              bgcolor: blue[100],
              fontWeight: 600,
            }}
          >
            {groupName}
          </ListSubheader>

          {/* Group Fields */}
          {cols.map(({ key, type, alias }) => {
            const label = alias || key;
            const value = user[key];

            return (
              <ListItem key={key} sx={{ px: 2 }}>
                {type === "Date" ? (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={label}
                      format="YYYY-MM-DD"
                      value={dayjs(value).isValid() ? dayjs(value) : null}
                      onChange={(newValue) =>
                        handleDateChange(newValue, key, "Date")
                      }
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                ) : type === "Time" ? (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimeField
                      label={label}
                      format="HH:mm"
                      value={value || ""}
                      onChange={(newValue) =>
                        handleTimeChange(newValue, key, "Time")
                      }
                      fullWidth
                    />
                  </LocalizationProvider>
                ) : (
                  <TextField
                    label={label}
                    value={value || ""}
                    onChange={(e) =>
                      handleInputChange(e.target.value, key, "raw")
                    }
                    fullWidth
                  />
                )}
              </ListItem>
            );
          })}
        </Box>
      ))}
    </List>
  );
}

